import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IProductCard } from '@models/IProductCard';
import AddToWishlist from '@molecules/AddToWishlist/AddToWishlist';
import DetailHeroInfo from '@molecules/DetailHeroInfo/DetailHeroInfo';
import PaintCalculator, {
  IPaintCalculator,
} from '@organisms/PaintCalculator/PaintCalculator';
import { fadeInUpSlow } from '@utility/config/animations';
import { motion } from 'motion/react';
interface Props extends IProductCard {
  readonly badges?: IImage[];
  readonly colourCta?: ILink;
  readonly description?: string;
  readonly findDealerCta?: ILink;
  readonly paintCalculator?: IPaintCalculator;
  readonly productPageLink: ILink;
}

const ProductDetailInfo = ({
  badges,
  category,
  colourCta,
  description,
  findDealerCta,
  images,
  paintCalculator,
  productImage,
  productName,
  productPageLink,
  productRole,
  tags,
  usps,
  wishlistDrawerLabels,
}: Props) => {
  const mainImage = {
    ...productImage.mobile,
    srcSet: productImage.desktop.url,
  };

  const card = {
    category,
    cta: productPageLink,
    images,
    productImage,
    productName,
    productRole,
    tags,
    usps,
  };

  const showPaintCalculator = paintCalculator?.productPaintCoverageValue !== undefined && paintCalculator?.productPaintCoatsValue !== undefined;

  const WishlistButton = () => (
    <AddToWishlist
      isCard={false}
      card={card as IProductCard}
      cardId={productPageLink.url}
      collection={CardTypeEnum.Products}
      wishlistDrawerLabels={wishlistDrawerLabels}
      buttonVariant="white"
    />
  );

  return (
    <DetailHeroInfo
      site={SiteEnum.Deco}
      mainImage={mainImage}
      images={images}
      trumpetText={category}
      title={productName}
      variant="product"
      tags={tags}
      wishList={wishlistDrawerLabels ? <WishlistButton /> : undefined}
    >
      {(description || !!usps.length || !!badges?.length) && (
        <div className="tw-flex tw-w-full tw-flex-col tw-divide-y-1 tw-divide-grey-20">
          {description && (
            <motion.div
              variants={fadeInUpSlow}
              className="tw-pb-md tw-font-sans tw-text-lg tw-font-light tw-text-black lg:tw-text-xl lg:first-letter:tw-mt-4"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <div className="tw-relative tw-flex tw-w-full tw-flex-wrap tw-items-start tw-justify-between tw-gap-xs tw-pt-md lg:tw-flex-nowrap">
            {!!usps.length && (
              <motion.ul
                variants={fadeInUpSlow}
                transition={{ ease: 'easeInOut', duration: 0.3 }}
                className="tw-will-change-opacity tw-min-h-xl tw-list-disc tw-px-xs tw-text-left tw-text-xs tw-text-grey-70 rtl:tw-text-right"
              >
                {usps.map((usp) => (
                  <li key={usp}>
                    <Text variant="body">{usp}</Text>
                  </li>
                ))}
              </motion.ul>
            )}
            {!!badges?.length && (
              <motion.div
                variants={fadeInUpSlow}
                transition={{ ease: 'easeInOut', duration: 0.3 }}
                className="tw-flex tw-flex-wrap tw-justify-start tw-gap-x-xxs tw-gap-y-0.5 lg:tw-w-1/2 lg:tw-justify-end xl:tw-w-[27%]"
              >
                {badges.map((badge) => (
                  <Image
                    key={badge.meta?.contentLink}
                    image={badge}
                    width={40}
                    height={40}
                    classes="tw-w-10 tw-h-10"
                  />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      )}
      {(colourCta?.url || findDealerCta?.url || paintCalculator) && (
        <motion.div
          variants={fadeInUpSlow}
          className="tw-flex tw-w-full tw-gap-xxs lg:tw-gap-xs"
        >
          {showPaintCalculator && (
            <PaintCalculator
              paintCalculator={paintCalculator}
              productName={productName}
              productUrl={productPageLink.url}
            />
          )}
          {colourCta?.url && (
            <Button
              buttonClasses={
                findDealerCta?.url ? 'tw-basis-6/12' : 'tw-basis-full'
              }
              as="link"
              href={colourCta.url}
              variant="grey80"
              modifier="outline"
              label={colourCta.text}
              target={colourCta.target}
            />
          )}
          {findDealerCta?.url && (
            <Button
              buttonClasses={colourCta?.url ? 'tw-basis-6/12' : 'tw-basis-full'}
              as="link"
              href={findDealerCta.url}
              variant="grey80"
              modifier="fill"
              label={findDealerCta.text}
              target={findDealerCta.target}
            />
          )}
        </motion.div>
      )}
    </DetailHeroInfo>
  );
};

export default ProductDetailInfo;
